import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'

const scientificPillars = [
  {
    number: '01',
    title: 'Ricalibrazione Dopaminergica (Neuro-Hacking)',
    subtitle: 'Riorientamento dei circuiti del desiderio e della ricompensa',
    desc: 'La procrastinazione non è pigrizia, ma un disallineamento della dopamina. Il mondo virtuale offre stimoli istantanei a sforzo zero. Il nostro metodo ti insegna a ripristinare i recettori basali (tonic dopamine) riducendo la frizione di avvio offline attraverso micro-missioni strutturate a tempo limitato.',
    details: 'Utilizziamo la tecnica del "Pomodoro a Barra Corta" (15 minuti) per disinnescare la resistenza iniziale dell\'amigdala e indurre lo stato di flusso senza sovraccarichi cognitivi.',
    color: '#8b5cf6',
  },
  {
    number: '02',
    title: 'Biomeccanica e Sistema Nervoso Autonomo',
    subtitle: 'Il corpo come hardware principale della mente',
    desc: 'Il tuo cervello riceve l\'80% delle informazioni dal corpo (via afferente). Se passi 10 ore seduto in postura di chiusura, il tuo sistema simpatico interpreta la contrazione come uno stato di pericolo costante, innalzando il cortisolo. Ricolleghiamo mente e hardware corporeo per sbloccare l\'azione.',
    details: 'Integriamo principi di sport da combattimento (Kickboxing), sblocco articolare e rieducazione posturale per elevare la stamina fisica e stabilizzare la chiarezza mentale.',
    color: '#ef4444',
  },
  {
    number: '03',
    title: 'Gamification Comportamentale Reale',
    subtitle: 'Tradurre la lore virtuale in XP offline',
    desc: 'Nessun gamer si lamenta di dover grindare per salire di livello: lo fa perché vede i progressi, le statistiche e le ricompense in modo chiaro. Trasformiamo le tue abitudini quotidiane (studio, allenamento, routine) in un foglio personaggio RPG cartaceo ed in sub-quest ad ambiguità zero.',
    details: 'Creiamo una mappa visiva delle tue statistiche (STR, DEX, INT, WIS) e tracciamo i tuoi checkpoint in modo tangibile, convertendo l\'autodisciplina in un gameplay gratificante.',
    color: '#00d4ff',
  },
]

const bioHacks = [
  {
    name: 'Sospiro Fisiologico (Physiological Sigh)',
    trigger: 'Per spegnere l\'ansia acuta e lo stress in 30 secondi',
    protocol: 'Esegui due inspirazioni rapide e consecutive dal naso (la prima profonda, la seconda corta per riaprire gli alveoli polmonari), seguite da un\'espirazione lentissima e prolungata dalla bocca. Ripeti 2-3 volte per attivare istantaneamente il sistema parasimpatico.',
    color: '#8b5cf6',
  },
  {
    name: 'Box Breathing (Respirazione Quadrata)',
    trigger: 'Per stabilizzare la frequenza cardiaca ed entrare in focus',
    protocol: 'Visualizza un quadrato: inspira per 4 secondi, trattieni il respiro a polmoni pieni per 4 secondi, espira completamente per 4 secondi, trattieni a polmoni vuoti per 4 secondi. Ripeti per 2 minuti per allineare il battito cardiaco (HRV).',
    color: '#00d4ff',
  },
  {
    name: 'Il Timer dei 15 Minuti',
    trigger: 'Per distruggere la procrastinazione e la resistenza iniziale',
    protocol: 'Quando il cervello si blocca e rifiuta un compito complesso, imposta un timer visivo di soli 15 minuti. Diti che puoi fermarti allo scadere. Il 90% delle volte, una volta superata la barriera dopaminergica dei primi 5 minuti, continuerai a lavorare.',
    color: '#facc15',
  },
]

const comparisonData = [
  { feature: 'Focus Primario', generic: 'Forza di volontà bruta ("Svegliati alle 5 AM e spingi")', nerdCoach: 'Neuro-calibrazione (Lavoriamo con la chimica del cervello, non contro)' },
  { feature: 'Hardware Fisico', generic: 'Schede di palestra prestampate e dieta restrittiva noiosa', nerdCoach: 'Biomeccanica integrata, tecniche respiratorie ed espressione motoria reale' },
  { feature: 'Misurazione Progressi', generic: 'App di tracking complicate che causano ulteriori distrazioni', nerdCoach: 'Registro di Gilda cartaceo, statistiche RPG reali e sub-quest a scadenze' },
  { feature: 'Rapporto con il Gaming', generic: 'Rinnegare le passioni, demonizzare schermi ed isolarsi offline', nerdCoach: 'Convertire le abilità sinaptiche virtuali in superpoteri nella vita offline' },
]

export default function MethodPage() {
  const introRef = useRef(null)
  const pillarsRef = useRef(null)
  const hacksRef = useRef(null)
  const stepsRef = useRef(null)

  const introInView = useInView(introRef, { once: true, margin: '-50px' })
  const pillarsInView = useInView(pillarsRef, { once: true, margin: '-50px' })
  const hacksInView = useInView(hacksRef, { once: true, margin: '-50px' })
  const stepsInView = useInView(stepsRef, { once: true, margin: '-50px' })

  return (
    <>
      <SEO 
        title="Il Metodo di Neuro-Leveling | Mente & Biomeccanica"
        description="Scopri la scienza dietro il coaching di Nerd Coach. Neurobiologia del focus, biomeccanica del movimento corporeo e gamification reale per fare il respawn offline."
      />

      <div className="min-h-screen bg-[#050510] text-[#f5f5f5] pt-32 pb-24 relative overflow-hidden">
        {/* Cyberpunk grid overlay background */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(139,92,246,0.01) 50px, rgba(139,92,246,0.01) 51px), repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(139,92,246,0.01) 50px, rgba(139,92,246,0.01) 51px)' }} />

        <div className="max-w-6xl mx-auto px-4 relative z-10 space-y-24">
          
          {/* SEZIONE 1 — INTRO SCIENTIFICA */}
          <section ref={introRef} className="text-center space-y-6 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={introInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="space-y-4"
            >
              <span className="text-[10px] text-nerd-purple font-display uppercase tracking-[0.3em] font-bold">Protocollo Operativo</span>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-nerd-white">
                Non motivazione temporanea.<br />
                <span className="text-gradient">Solo sistemi biologici.</span>
              </h1>
              <p className="text-nerd-gray text-base sm:text-lg md:text-xl leading-relaxed font-body max-w-2xl mx-auto">
                La maggior parte della produttività generica fallisce perché ti chiede di forzare il tuo cervello tramite la pura forza di volontà. Noi lavoriamo sulla neurobiologia del focus, sulla biomeccanica articolare e sulla regolazione del sistema nervoso autonomo.
              </p>
            </motion.div>
          </section>

          {/* SEZIONE 2 — I TRE PILASTRI DEL NEURO-LEVELING */}
          <section ref={pillarsRef} className="space-y-12">
            <div className="text-center space-y-2">
              <span className="text-[9px] text-nerd-cyan font-display uppercase tracking-widest font-bold">Le Tre Direttrici</span>
              <h2 className="font-display text-3xl font-bold text-nerd-white">I Pilastri Biologici del Metodo</h2>
              <p className="text-nerd-gray text-xs sm:text-sm font-body max-w-md mx-auto">
                Alcuni esempi di come interveniamo per ricalibrare le tue abitudini e il tuo corpo offline.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {scientificPillars.map((p, idx) => (
                <motion.div
                  key={p.number}
                  className="manga-panel rounded-xl p-6 md:p-8 flex flex-col justify-between relative overflow-hidden"
                  style={{ border: `1px solid ${p.color}25` }}
                  initial={{ opacity: 0, y: 40 }}
                  animate={pillarsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: idx * 0.15 }}
                >
                  <div className="space-y-4">
                    <div className="flex justify-between items-baseline">
                      <span className="font-display text-xs font-bold uppercase tracking-wider" style={{ color: p.color }}>
                        Pillar {p.number}
                      </span>
                      <span className="text-[32px] font-display font-bold opacity-10" style={{ color: p.color }}>
                        {p.number}
                      </span>
                    </div>

                    <h3 className="font-display text-xl font-bold text-nerd-white leading-snug">
                      {p.title}
                    </h3>
                    <span className="text-[10px] uppercase font-display tracking-widest text-nerd-gray block font-semibold">
                      {p.subtitle}
                    </span>

                    <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                      {p.desc}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-nerd-purple/10">
                    <span className="text-[9px] text-nerd-cyan font-display uppercase font-bold tracking-widest block mb-1">Implementazione pratica:</span>
                    <p className="text-nerd-white text-xs font-body font-medium leading-relaxed">
                      {p.details}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* SEZIONE 3 — TAVOLO COMPARATIVO PREMIUM */}
          <section className="manga-panel rounded-2xl p-6 md:p-10 border-nerd-purple/20 bg-nerd-dark/40 relative overflow-hidden">
            {/* Corner brackets */}
            <div className="absolute top-2 left-2 w-4 h-4 border-l border-t border-nerd-purple/30" />
            <div className="absolute top-2 right-2 w-4 h-4 border-r border-t border-nerd-purple/30" />
            <div className="absolute bottom-2 left-2 w-4 h-4 border-l border-b border-nerd-purple/30" />
            <div className="absolute bottom-2 right-2 w-4 h-4 border-r border-b border-nerd-purple/30" />

            <div className="space-y-8">
              <div className="text-center md:text-left space-y-2">
                <h3 className="font-display text-2xl font-bold text-nerd-white">La Differenza Strategica</h3>
                <p className="text-nerd-gray text-xs sm:text-sm font-body">Perché il coaching tradizionale non funziona con il cervello di un gamer o programmatore.</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs sm:text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-nerd-purple/20 text-nerd-gray font-display uppercase tracking-widest">
                      <th className="py-3 px-4">Caratteristica</th>
                      <th className="py-3 px-4">Coaching Tradizionale</th>
                      <th className="py-3 px-4 text-nerd-cyan font-bold">Metodo Nerd Coach</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-nerd-purple/10 font-body">
                    {comparisonData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-nerd-purple/5 transition-colors">
                        <td className="py-4 px-4 font-display font-semibold text-nerd-white">{row.feature}</td>
                        <td className="py-4 px-4 text-nerd-gray">{row.generic}</td>
                        <td className="py-4 px-4 text-nerd-white font-medium bg-nerd-cyan/5 border-l-2 border-nerd-cyan">{row.nerdCoach}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* SEZIONE 4 — INTERACTIVE BIO-HACKS */}
          <section ref={hacksRef} className="space-y-12">
            <div className="text-center space-y-2">
              <span className="text-[9px] text-nerd-purple font-display uppercase tracking-widest font-bold">Tech Tree: Utility</span>
              <h2 className="font-display text-3xl font-bold text-nerd-white">Bio-Hacks di Sblocco Istantaneo</h2>
              <p className="text-nerd-gray text-xs sm:text-sm font-body max-w-md mx-auto">
                Tre protocolli neurofisiologici pratici che puoi inserire fin da subito nella tua giornata offline per sbloccare l'azione.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {bioHacks.map((hack, idx) => (
                <motion.div
                  key={hack.name}
                  className="manga-panel rounded-xl p-5 relative overflow-hidden bg-nerd-dark/20 border border-nerd-cyan/15 flex flex-col justify-between"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={hacksInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.15 }}
                >
                  <div className="space-y-3">
                    <span className="text-[8px] text-nerd-cyan uppercase font-display font-bold tracking-widest block">
                      Trigger: {hack.trigger}
                    </span>
                    <h3 className="font-display text-base font-bold text-nerd-white" style={{ color: hack.color }}>
                      {hack.name}
                    </h3>
                    <p className="text-nerd-gray text-xs leading-relaxed font-body">
                      {hack.protocol}
                    </p>
                  </div>

                  <div className="mt-4 flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 rounded bg-nerd-purple/10 border border-nerd-purple/35 flex items-center justify-center text-nerd-purple text-[8px] font-bold">
                      ✓
                    </span>
                    <span className="text-[7px] text-nerd-purple uppercase font-display font-semibold tracking-wider">Protocollo Scientifico</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* SEZIONE 5 — LA PROGRESSIONE COOPERATIVA (ONBOARDING) */}
          <section ref={stepsRef} className="space-y-12">
            <div className="text-center space-y-2">
              <span className="text-[9px] text-nerd-cyan font-display uppercase tracking-widest font-bold">La Campagna</span>
              <h2 className="font-display text-3xl font-bold text-nerd-white">Le 3 Fasi del Percorso Cooperativo</h2>
              <p className="text-nerd-gray text-xs sm:text-sm font-body max-w-md mx-auto">
                Come lavoriamo insieme, passo dopo passo, per forgiare la tua build strategica offline.
              </p>
            </div>

            <div className="relative">
              {/* Central timeline line */}
              <div className="absolute top-0 bottom-0 left-[20px] md:left-1/2 w-[1px] bg-gradient-to-b from-nerd-purple/30 via-nerd-cyan/30 to-transparent -translate-x-1/2" />

              <div className="space-y-12">
                
                {/* FASE 1 */}
                <motion.div
                  className="relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-12"
                  initial={{ opacity: 0, x: -40 }}
                  animate={stepsInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6 }}
                >
                  <div className="flex-1 md:text-right">
                    <div className="manga-panel rounded-xl p-6 ml-[40px] md:ml-0 md:mr-6 border border-nerd-purple/20 bg-nerd-bg">
                      <span className="text-[8px] text-nerd-purple font-display uppercase tracking-widest font-bold block mb-1">Fase 01</span>
                      <h3 className="font-display text-lg font-bold text-nerd-white mb-2">Mappatura Iniziale (360° Assessment)</h3>
                      <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                        Analizziamo in profondità il tuo orologio biologico (cronotipo), il tuo livello attuale di tolleranza allo stress (tono vagale), la tua nutrizione e i trigger comportamentali che attivano i tuoi loop di evitamento davanti agli schermi.
                      </p>
                    </div>
                  </div>

                  <div className="absolute left-[20px] md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-2 border-nerd-purple flex items-center justify-center bg-nerd-bg z-10">
                    <span className="text-xs font-display font-bold text-nerd-purple">1</span>
                  </div>

                  <div className="flex-1 hidden md:block" />
                </motion.div>

                {/* FASE 2 */}
                <motion.div
                  className="relative flex flex-col md:flex-row-reverse items-start md:items-center gap-6 md:gap-12"
                  initial={{ opacity: 0, x: 40 }}
                  animate={stepsInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="flex-1 text-left">
                    <div className="manga-panel rounded-xl p-6 ml-[40px] md:ml-6 md:mr-0 border border-nerd-cyan/20 bg-nerd-bg">
                      <span className="text-[8px] text-nerd-cyan font-display uppercase tracking-widest font-bold block mb-1">Fase 02</span>
                      <h3 className="font-display text-lg font-bold text-nerd-white mb-2">Configurazione della Build Personalizzata</h3>
                      <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                        Costruiamo il tuo protocollo strategico offline. Definiamo i tuoi task in micro-quest chiare a frizione dopaminergica zero, strutturiamo la tua routine di mobilità fisica e ottimizziamo le tue abitudini di stile di vita e nutrizionali quotidiane per darti energia senza crolli di focus.
                      </p>
                    </div>
                  </div>

                  <div className="absolute left-[20px] md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-2 border-nerd-cyan flex items-center justify-center bg-nerd-bg z-10">
                    <span className="text-xs font-display font-bold text-nerd-cyan">2</span>
                  </div>

                  <div className="flex-1 hidden md:block" />
                </motion.div>

                {/* FASE 3 */}
                <motion.div
                  className="relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-12"
                  initial={{ opacity: 0, x: -40 }}
                  animate={stepsInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <div className="flex-1 md:text-right">
                    <div className="manga-panel rounded-xl p-6 ml-[40px] md:ml-0 md:mr-6 border border-nerd-gold/20 bg-nerd-bg">
                      <span className="text-[8px] text-nerd-gold font-display uppercase tracking-widest font-bold block mb-1">Fase 03</span>
                      <h3 className="font-display text-lg font-bold text-nerd-white mb-2">Raid Settimanali (Coaching 1-on-1)</h3>
                      <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">
                        Nessuno vince la campagna da solo. Ogni settimana facciamo un check-in approfondito 1-on-1: analizziamo i tuoi registri cartacei, ricalibriamo le metriche di sonno e movimento, superiamo i blocchi settimanali e modifichiamo le variabili per salire di livello offline.
                      </p>
                    </div>
                  </div>

                  <div className="absolute left-[20px] md:left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-2 border-nerd-gold flex items-center justify-center bg-nerd-bg z-10">
                    <span className="text-xs font-display font-bold text-nerd-gold">3</span>
                  </div>

                  <div className="flex-1 hidden md:block" />
                </motion.div>

              </div>
            </div>
          </section>

          {/* CALL TO ACTION EPICA FINALE */}
          <div className="text-center py-10 border-t border-nerd-purple/10">
            <h3 className="font-display text-2xl font-bold text-nerd-white mb-4">Sei pronto ad uscire dal loop passivo ed avviare il tuo sistema?</h3>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Link to="/#lead-game" className="nerd-btn-primary text-sm inline-block w-full sm:w-auto">
                Gioca il Libro-Game in Home 🎮
              </Link>
              <Link to="/nerd-power" className="nerd-btn-secondary text-sm inline-block w-full sm:w-auto">
                Calibra il tuo Nerd Power ⚡
              </Link>
            </div>
          </div>

          <div className="text-center mt-12 max-w-2xl mx-auto opacity-50 text-[10px] font-body text-nerd-gray leading-relaxed">
            * Nota Legale / Disclaimer: Il percorso di coaching non prevede la prescrizione di diete mediche o piani nutrizionali terapeutici (attività strettamente riservate a figure professionali sanitarie abilitate come biologi nutrizionisti e dietologi), ma si focalizza esclusivamente sull'ottimizzazione e il miglioramento delle abitudini comportamentali, dello stile di vita quotidiano, dell'idratazione e dell'educazione alimentare a scopo di benessere e performance personale offline.
          </div>

        </div>
      </div>
    </>
  )
}
